import type { WindowSummary } from '@core/line-probe';
import { percentile } from './perSecond';

/** One row of the summary table: a sealed window, or the whole run folded from them. */
export type SummaryRow = Omit<WindowSummary, 'target' | 'window' | 'windowStart' | 'transport' | 'loadLevel' | 'loadTargetKbps'> & {
    transport: WindowSummary['transport'] | 'mixed';
};

const sum = (xs: number[]) => xs.reduce((n, x) => n + x, 0);
/** Null when every window had none (a count that only exists over UDP, or only over TCP). */
const sumOrNull = (xs: (number | null)[]) => (xs.every((x) => x === null) ? null : sum(xs.filter((x): x is number => x !== null)));
const medianOf = (xs: (number | null)[]) =>
    percentile(
        xs.filter((x): x is number => x !== null),
        0.5
    );
const maxOf = (xs: (number | null)[]) => {
    const v = xs.filter((x): x is number => x !== null);
    return v.length ? Math.max(...v) : null;
};

/**
 * The whole run of one target from its sealed windows (spec 4.4): counts and times add up, the
 * maxima are the maximum, and the percentiles are the median of the windows' percentiles -- the
 * windows are what was reported, their probes are gone. Load rates average over the windows in
 * which a load ran.
 */
export function summariseRun(windows: WindowSummary[]): SummaryRow | null {
    if (windows.length === 0) return null;
    const transports = new Set(windows.map((w) => w.transport).filter((t) => t !== 'none'));
    const loaded = windows.filter((w) => w.loadLevel !== 'off');
    const pick = <K extends keyof WindowSummary>(key: K) => windows.map((w) => w[key]);
    return {
        transport: transports.size === 0 ? 'none' : transports.size === 1 ? [...transports][0] : 'mixed',
        sent: sum(pick('sent')),
        echoed: sum(pick('echoed')),
        lostUp: sumOrNull(pick('lostUp')),
        lostDown: sumOrNull(pick('lostDown')),
        lostUnknown: sumOrNull(pick('lostUnknown')),
        late: sumOrNull(pick('late')),
        rttP50: medianOf(pick('rttP50')),
        rttP95: medianOf(pick('rttP95')),
        rttMax: maxOf(pick('rttMax')),
        jitterP50: medianOf(pick('jitterP50')),
        jitterP95: medianOf(pick('jitterP95')),
        jitterUpP50: medianOf(pick('jitterUpP50')),
        jitterUpP95: medianOf(pick('jitterUpP95')),
        silenceMaxMs: Math.max(...pick('silenceMaxMs')),
        measuredMs: sum(pick('measuredMs')),
        stalledMs: sum(pick('stalledMs')),
        blockedMs: sum(pick('blockedMs')),
        noSessionMs: sum(pick('noSessionMs')),
        backgroundMs: sum(pick('backgroundMs')),
        loadSentKbps: loaded.length ? Math.round(sum(loaded.map((w) => w.loadSentKbps)) / loaded.length) : 0,
        loadReceivedKbps: loaded.length ? Math.round(sum(loaded.map((w) => w.loadReceivedKbps)) / loaded.length) : 0
    };
}
